import { useEffect, useRef, useState } from 'react';
import { BrandCategory } from '@/interfaces/Menu';
import styles from './MenuCategories.module.scss';
import MenuCategory from './MenuCategory';

interface MenuCategoriesProps {
  categories: BrandCategory[];
  onCategorySelect?: (category: BrandCategory, shouldScroll?: boolean) => void;
  activeCategory?: BrandCategory | null;
  onOpenCategoriesSheet: () => void;
}

const MenuCategories = ({
  categories,
  onCategorySelect,
  activeCategory,
  onOpenCategoriesSheet,
}: MenuCategoriesProps) => {
  const [isSticky, setIsSticky] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Set first category as active on initial load
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      const firstCategory = categories[0];
      onCategorySelect?.(firstCategory, false); // Don't scroll on initial load
    }
  }, [categories, activeCategory, onCategorySelect]);

  // Auto-scroll category menu when activeCategory changes (from scroll)
  useEffect(() => {
    if (activeCategory) {
      const categoryElement = document.querySelector(
        `[data-category-id="${activeCategory.id}"]`
      ) as HTMLElement;

      if (categoryElement) {
        const container = categoryElement.closest(`.${styles.categoriesList}`) as HTMLElement;
        if (container) {
          const containerWidth = container.offsetWidth;
          const elementLeft = categoryElement.offsetLeft;
          const elementWidth = categoryElement.offsetWidth;

          // Calculate scroll position to show category as far left as possible
          const scrollLeft = elementLeft - containerWidth / 2 + elementWidth / 2;

          container.scrollTo({
            left: Math.max(0, scrollLeft),
            behavior: 'smooth',
          });
        }
      }
    }
  }, [activeCategory]);

  // Intersection Observer to detect when menu should stick
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleCategoryClick = (category: BrandCategory) => {
    onCategorySelect?.(category, true); // Scroll when user clicks

    // Scroll the category menu to show the selected category
    const categoryElement = document.querySelector(
      `[data-category-id="${category.id}"]`
    ) as HTMLElement;
    if (categoryElement) {
      const container = categoryElement.closest(`.${styles.categoriesList}`) as HTMLElement;
      if (container) {
        const containerWidth = container.offsetWidth;
        const elementLeft = categoryElement.offsetLeft;
        const elementWidth = categoryElement.offsetWidth;

        // Calculate scroll position to show category as far left as possible
        const scrollLeft = elementLeft - containerWidth / 2 + elementWidth / 2;

        container.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth',
        });
      }
    }
  };

  return (
    <>
      {/* Invisible sentinel element to detect when menu should stick */}
      <div ref={sentinelRef} style={{ height: '1px', margin: '-1px 0' }} />

      {/* Placeholder to prevent content jump when menu becomes sticky */}
      {isSticky && (
        <div
          style={{
            height: window.innerWidth >= 768 ? '64px' : '44px',
          }}
        />
      )}

      <div
        ref={containerRef}
        className={`${styles.container} ${isSticky ? styles.sticky : ''}`}
        data-testid="menu-categories"
      >
        <div className={styles.categoriesWrapper}>
          <div className={styles.categoriesList}>
            {categories.map(category => (
              <MenuCategory
                key={category.id}
                category={category}
                isActive={activeCategory?.id === category.id}
                onClick={() => handleCategoryClick(category)}
                data-category-id={category.id}
              />
            ))}
          </div>
        </div>

        <div className={styles.menuButton}>
          <button
            className={styles.hamburger}
            onClick={onOpenCategoriesSheet}
            data-testid="menu-categories-hamburger"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
    </>
  );
};

export default MenuCategories;
